import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AbstractControl, UntypedFormGroup, UntypedFormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Observable, of, timer } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ITrainingProgram } from '../../../../models/training/training-program';
import { IPracticumSiteOregon } from 'app/models/training/practicum-site-oregon';
import { WorkerService } from 'app/services/licenses/worker.service';
import { SharedService } from 'app/services/core/shared.service';

@Component({
    selector: 'app-add-oregon-training-modal',
    templateUrl: './add-oregon-training-modal.component.html',
    styleUrls: ['./add-oregon-training-modal.component.scss'],
    standalone: false
})
export class AddOregonTrainingModalComponent {

  public trainingForm = new UntypedFormGroup({
    programId: new UntypedFormControl('', [Validators.required]),
    supervisor: new UntypedFormControl('', [Validators.required, Validators.maxLength(100)]),
    supervisorFrom: new UntypedFormControl('', [Validators.required]),
    workerPermitNumber: new UntypedFormControl('', [this.workerPermitNumberRequiredValidator()], [this.validateWorkerPermitNumber.bind(this)])
  });

  public trainingPrograms: ITrainingProgram[] = [];
  public editMode: boolean = false;

  constructor(
    public sharedService: SharedService,
    public dialogRef: MatDialogRef<AddOregonTrainingModalComponent>,
    private workerService: WorkerService,
    @Inject(MAT_DIALOG_DATA) public data: {
      trainingPrograms: ITrainingProgram[],
      editMode?: boolean,
      existingSite?: IPracticumSiteOregon
    }
  ) {
    this.trainingPrograms = data.trainingPrograms;
    this.editMode = data.editMode || false;

    if (this.editMode && data.existingSite) {
      this.trainingForm.patchValue({
        programId: data.existingSite.programId,
        supervisor: data.existingSite.supervisor,
        supervisorFrom: data.existingSite.supervisorFrom,
        workerPermitNumber: data.existingSite.workerPermitNumber
      });
      // Disable program selection in edit mode
      this.trainingForm.get('programId').disable();
    }
    if(this.sharedService.isAfterJan15th2027()) {
       this.trainingForm.get('supervisor').disable();
    }
  }

  private workerPermitNumberRequiredValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!this.sharedService.isAfterJan15th2027()) {
        return null;
      }
      return Validators.required(control);
    };
  }

  private validateWorkerPermitNumber(control: AbstractControl): Observable<ValidationErrors | null> {
    const permitNumber = control.value;
    if (!permitNumber || !this.sharedService.isAfterJan15th2027()) {
      return of(null);
    }

    return timer(500).pipe(
      switchMap(() => this.workerService.getWorkerPermitName(permitNumber)),
      map(permitName => {
        this.trainingForm.get('supervisor').setValue(permitName || '', { emitEvent: false });
        return permitName ? null : { permitNotFound: true };
      }),
      catchError(() => {
        this.trainingForm.get('supervisor').setValue('', { emitEvent: false });
        return of({ permitNotFound: true });
      })
    );
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (this.trainingForm.pending) {
      return;
    }
    if (this.trainingForm.valid || this.editMode) {
      const selectedProgramId = this.editMode ? this.data.existingSite.programId : this.trainingForm.get('programId').value;

      const selectedProgram = this.trainingPrograms.find(tp => tp.programId === selectedProgramId);

      if (!selectedProgram && !this.editMode) {
        console.error('Training program not found');
        return;
      }

      const result: IPracticumSiteOregon = {
        id: this.editMode ? this.data.existingSite.id : 0,
        programId: selectedProgramId,
        trainingProgramId: this.editMode ? this.data.existingSite.trainingProgramId : selectedProgram.id,
        supervisor: this.trainingForm.get('supervisor').value,
        supervisorFrom: this.trainingForm.get('supervisorFrom').value,
        workerPermitNumber: this.trainingForm.get('workerPermitNumber').value,
        programName: this.editMode ? this.data.existingSite.programName : selectedProgram.programName,
        archived: false,
        trainingProgramName: this.editMode ? this.data.existingSite.trainingProgramName : selectedProgram.programName
      };
      this.dialogRef.close(result);
    }
  }
}
